import { Input, Label } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { EUR_KEY_SUFFIX, type FeesTableRow } from '@/services/api/risk-fees';
import { formatEuros } from '@/utils/format';

/** A full default 30-day range (31 UTC days inclusive) fits on one page. */
const TABLE_PAGE_SIZE = 31;
const MIN_FRACTION_DIGITS = 2;
const MAX_FRACTION_DIGITS = 8;

// Legacy cell format: native amount then EUR in parentheses,
// e.g. "0.00312400 (€152.11)". Raw numbers stay on the row; formatting is cell-only.
const formatFeeCell = (raw: number, eur: number): string =>
  `${raw.toLocaleString(undefined, { minimumFractionDigits: MIN_FRACTION_DIGITS, maximumFractionDigits: MAX_FRACTION_DIGITS })} (${formatEuros(eur)})`;

const DATE_COLUMN: ColumnDef<FeesTableRow, unknown> = {
  accessorKey: 'date',
  header: 'Date',
  cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{row.original.date}</span>,
};

interface FeesTableProps {
  rows: FeesTableRow[];
  /** Base currency codes, already sorted alphabetically */
  currencies: string[];
}

/** Per-UTC-day fees table with one dynamic column per currency in the response window. */
export const FeesTable = ({ rows, currencies }: FeesTableProps) => {
  const [dateFilter, setDateFilter] = useState('');

  const columns = useMemo(
    (): ColumnDef<FeesTableRow, unknown>[] => [
      DATE_COLUMN,
      ...currencies.map(
        (currency): ColumnDef<FeesTableRow, unknown> => ({
          // accessorKey = base code → sorting compares the RAW native amount
          accessorKey: currency,
          header: currency,
          cell: ({ row }) => (
            <span className="whitespace-nowrap tabular-nums">{formatFeeCell(Number(row.original[currency] ?? 0), Number(row.original[`${currency}${EUR_KEY_SUFFIX}`] ?? 0))}</span>
          ),
        }),
      ),
    ],
    [currencies],
  );

  // The shared DataTable has no per-column filters — the legacy Date column text
  // filter is ported as this panel-level input (flagged deviation).
  const filteredRows = useMemo(() => {
    const query = dateFilter.trim();
    return query ? rows.filter((row) => row.date.includes(query)) : rows;
  }, [rows, dateFilter]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="fees-date-filter">Filter dates</Label>
        <Input className="w-44" id="fees-date-filter" onChange={(event) => setDateFilter(event.target.value)} placeholder="YYYY-MM-DD" value={dateFilter} />
      </div>
      <DataTable columns={columns} data={filteredRows} emptyMessage="No collected fees in the selected range" pageSize={TABLE_PAGE_SIZE} />
    </section>
  );
};
FeesTable.displayName = 'FeesTable';
